<template>
    <div class="">
        <v-card rounded="lg">
            <v-card-title class="d-flex justify-space-between align-center py-6">
                <div class="">
                    <div v-if="form" class="text-h5 font-weight-black">
                        <div class="">{{ form.name }}</div>
                    </div>
                    <div v-else>
                        <div>N/A</div>
                    </div>
                    <p>{{ $t('i18n.viewFormAssociatedDevicesTitle') }}</p>
                </div>
                <v-btn icon="mdi-close" variant="text" @click="closeModal" size="x-large"></v-btn>
            </v-card-title>
            <v-card-text class="d-flex justify-space-between align-center">
                <v-skeleton-loader
                    v-if="loading"
                    type="table"
                ></v-skeleton-loader>
                <v-table v-else class="table" fixed-header :hover="detailedAssociatedDevicesList.length !== 0" height="300px" style="width: 100%;">
                    <thead>
                        <tr>
                            <th class="text-left font-weight-black cursor-pointer checkbox-cell">
                                <v-icon class="cursor-pointer pr-6"
                                    :icon="filterActive ? 'mdi-filter-remove' : 'mdi-filter-menu'"
                                    @click="filterActive = !filterActive"></v-icon>
                              {{ $t('i18n.name') }}
                            </th>
                            <th class="text-left font-weight-black">
                              {{ $t('i18n.location') }}
                            </th>
                            <th class="text-left font-weight-black">
                              {{ $t('i18n.type') }}
                            </th>
                            <th class="text-left font-weight-black">
                              {{ $t('i18n.ipAddress') }}
                            </th>
                            <th class="text-left font-weight-black">
                              {{ $t('i18n.shortURL') }}
                            </th>
                            <th class="text-left font-weight-black">
                              {{ $t('i18n.uniqueIdentifier') }}
                            </th>
                        </tr>
                        <tr v-if="filterActive">
                            <th class="text-left font-weight-black">
                                <v-text-field v-model="filters.name" density="compact" variant="outlined"
                                    :hide-details="true" clearable></v-text-field>
                            </th>
                            <th class="text-left font-weight-black">
                                <v-text-field v-model="filters.location" density="compact" variant="outlined"
                                    :hide-details="true" clearable></v-text-field>
                            </th>
                            <th class="text-left font-weight-black">
                                <v-text-field v-model="filters.type" density="compact" variant="outlined"
                                    :hide-details="true" clearable></v-text-field>
                            </th>
                            <th class="text-left font-weight-black">
                                <v-text-field v-model="filters.ip_address" density="compact" variant="outlined"
                                    :hide-details="true" clearable></v-text-field>
                            </th>
                            <th class="text-left font-weight-black">
                                <v-text-field v-model="filters.short_url" density="compact" variant="outlined"
                                    :hide-details="true" clearable></v-text-field>
                            </th>
                            <th class="text-left font-weight-black">
                                <v-text-field v-model="filters.uuid" density="compact" variant="outlined"
                                    :hide-details="true" clearable></v-text-field>
                            </th>
                        </tr>
                    </thead>
                    <tbody v-if="detailedAssociatedDevicesList.length !== 0">
                        <tr v-for="(device, index) in filteredDevices()" :key="index" class="text-left">
                            <td>{{ device.name }}</td>
                            <td>{{ device.location }}</td>
                            <td>{{ deviceTypeName(device.type_id) }}</td>
                            <td>{{ device.ip_address }}</td>
                            <td>{{ device.short_url }}</td>
                            <td>{{ device.uuid }}</td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                      <tr class="no-hover">
                        <td colspan="6" class="text-center"> {{ $t('i18n.noDataAvailable') }}</td>
                      </tr>
                    </tbody>
                </v-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script lang="ts">
import { Device, DeviceType, Form, SimplifiedDevice } from '@/utils/DataTypes'
import { useAppStore } from '@/stores/appStore'
import { api, findDeviceTypeById } from '@/utils/HelperFunctions'
import { defineComponent, PropType } from 'vue'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'ViewFormAssociatedDevices',
  setup () {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore
    }
  },
  props: {
    form: Object as PropType<Form>
  },
  async mounted () {
    this.deviceTypeList = this.appStore.getAppState.deviceTypeList
    await this.getAssociatedDevicesDetails()
    this.loading = false
  },
  data () {
    return {
      simplifiedAssociatedDevicesList: [] as SimplifiedDevice[],
      detailedAssociatedDevicesList: [] as Device[],
      deviceTypeList: [] as DeviceType[],

      filters: {
        name: '',
        location: '',
        type: '',
        ip_address: '',
        short_url: '',
        uuid: ''
      },

      filterActive: false,
      loading: true
    }
  },
  methods: {
    async getFormAssociatedDevices () {
      try {
        const url = '/forms-devices-association'
        const getFormDeviceAssociationResponse = await api(url, 'get')

        if (!getFormDeviceAssociationResponse) return

        const allFormDeviceAssociations = getFormDeviceAssociationResponse.data

        const formDeviceAssociationObject = allFormDeviceAssociations.find(
          (form: any) => form.id === this.form?.id
        )

        if (!formDeviceAssociationObject) {
          console.error('Form was not found.')
          throw new Error('Form was not found.')
        }

        return formDeviceAssociationObject.associated_devices ?? []
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get forms list.')
      }
    },
    async getAssociatedDevicesDetails () {
      const simplifiedAssociatedDevicesList = await this.getFormAssociatedDevices()
      const deviceDetailsPromises = simplifiedAssociatedDevicesList.map(async (device: SimplifiedDevice) => {
        try {
          const url = `/devices/${device.id}`
          const getDeviceResponse = await api(url, 'get')

          if (!getDeviceResponse) return

          const deviceObject = getDeviceResponse.data[0]
          return deviceObject
        } catch (error) {
          console.error(`Couldn't get details for device ${device.id}:`, error)
          return null
        }
      })

      this.detailedAssociatedDevicesList = await Promise.all(deviceDetailsPromises)
    },
    navigate (routeName: string) {
      this.router.push({ name: routeName })
    },
    closeModal () {
      this.$emit('close-modal')
    },
    deviceTypeName (typeID: number | null) {
      if (typeID === null) {
        return 'NULL'
      }
      const itemTypeName = findDeviceTypeById(this.deviceTypeList, typeID)
      if (itemTypeName === null) {
        return 'N/A'
      } else {
        return itemTypeName
      }
    },
    filteredDevices () {
      let filteredDevices = this.detailedAssociatedDevicesList as Device[]

      if (this.filters.name) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.name.toLowerCase().includes(this.filters.name.toLowerCase())
        )
      }
      if (this.filters.location) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.location?.toLowerCase().includes(this.filters.location.toLowerCase())
        )
      }
      if (this.filters.type) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          this.deviceTypeName(device.type_id).toLowerCase().includes(this.filters.type.toLowerCase())
        )
      }
      if (this.filters.ip_address) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.ip_address?.toLowerCase().includes(this.filters.ip_address.toLowerCase())
        )
      }
      if (this.filters.short_url) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.short_url?.toLowerCase().includes(this.filters.short_url.toLowerCase())
        )
      }
      if (this.filters.uuid) {
        filteredDevices = filteredDevices.filter((device: Device) =>
          device.uuid.toLowerCase().includes(this.filters.uuid.toLowerCase())
        )
      }
      return filteredDevices
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../../styles/colors.scss";

.form-associated-devices-container {
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100%;
}
</style>
